import { useEffect, useState } from 'react';
import logger from '../utils/logger';
import { useParams } from 'react-router-dom';
import { ContentFeed, ContentItem } from '../components/ContentFeed';
import { ErrorBoundary } from '../components/ErrorBoundary';

/**
 * Content section type
 * Defines the shape of a content section for the page
 */
interface ContentSectionType {
  name: string;
  slug: string;
  items: ContentItem[];
  itemCount: number;
  isEmpty: boolean;
}

/**
 * ContentSection page displays all items in a content section
 *
 * Features:
 * - Loads section content based on URL slug parameter
 * - Displays items in chronological feed (newest first)
 * - Loading and error states
 * - Responsive design
 *
 * NOTE: This page currently requires GraphQL migration to function.
 * The REST API endpoint has been removed.
 */
export function ContentSection() {
  const { slug } = useParams<{ slug: string }>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [section, _setSection] = useState<ContentSectionType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSection = async () => {
      if (!slug) {
        setError('No section specified');
        setLoading(false);
        return;
      }

      // TODO: Migrate to GraphQL - REST API has been removed
      logger.warn('ContentSection', 'Content API not available - needs GraphQL migration', { slug });
      setError('Content sections are being migrated to GraphQL. Please check back later.');
      setLoading(false);
    };

    loadSection();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          {error}
        </div>
      </div>
    );
  }

  if (!section) {
    return null;
  }

  return (
    <ErrorBoundary>
      <div>
        {/* Section header */}
        <div className="bg-surface-1 border-b border-border">
          <div className="container mx-auto px-4 py-6 max-w-4xl">
            <h1 className="text-3xl font-bold text-foreground">{section.name}</h1>
            <p className="text-sm text-muted mt-1">
              {section.itemCount} {section.itemCount === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>

        {/* Content feed */}
        <ContentFeed items={section.items || []} isEmpty={section.isEmpty} />
      </div>
    </ErrorBoundary>
  );
}
